

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Button
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useAuth } from 'auth/Auth';
import UserTable from './components/UserTable';
import RolePicker from 'components/RolePicker';
import CallCenterSeats from "components/SettingsComponents/CallCenterSeats";
import WhatsAppSeats from "components/SettingsComponents/WhatsAppSeats";
import AiAssistantSeats from "components/SettingsComponents/AiAssistantSeats";
import UsersAndSeatsV2 from "layouts/crm/users-v2/UsersAndSeatsV2";
import { useTheme } from "@mui/material/styles";
import { v2 as t, v2Tones } from "styles/tokens/v2";

import moment from 'moment';
import { frontendLogger } from 'utils/logger';

/** v2 licence summary: one band of facts instead of three boxed panels. */
function LicenceSummaryV2({ licenseInfo, onUpgrade }) {
  const expiry = licenseInfo.expiry_date ? new Date(licenseInfo.expiry_date) : null;
  const expiryText = expiry && !Number.isNaN(expiry.getTime()) ? expiry.toLocaleDateString() : "—";
  const tone = licenseInfo.status === "Active" ? v2Tones.success
    : licenseInfo.status === "Expiring Soon" ? v2Tones.warning
    : licenseInfo.status ? v2Tones.danger : v2Tones.neutral;
  const facts = [
    ["Plan", licenseInfo.license_type ? `${licenseInfo.license_type}${licenseInfo.is_trial ? " (Trial)" : ""}` : "—"],
    ["Billing", licenseInfo.billing_cycle || "—"],
    ["Seats used", `${licenseInfo.used_seats ?? 0} of ${licenseInfo.total_seats ?? 0}`],
    ["Available", String(licenseInfo.seats_remaining ?? 0)],
    ["Expires", expiryText],
    ["Days left", licenseInfo.remaining_days ?? "—"],
  ];
  return (
    <MDBox
      component="section"
      aria-label="Licence"
      sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, p: 2, mt: 2, mb: 2,
            border: `1px solid ${t.border}`, borderRadius: "10px", bgcolor: t.surface }}
    >
      <MDBox sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <MDBox component="h3" sx={{ m: 0, fontSize: 15, fontWeight: 600, color: t.text }}>Licence</MDBox>
        {licenseInfo.status && (
          <MDBox component="span" sx={{ px: 1, py: 0.25, borderRadius: "6px", fontSize: 12, fontWeight: 600, bgcolor: tone.bg, color: tone.fg }}>
            {licenseInfo.status}
          </MDBox>
        )}
      </MDBox>
      <MDBox component="dl" sx={{ m: 0, display: "flex", flexWrap: "wrap", columnGap: 3, rowGap: 1, flex: 1, minWidth: 0 }}>
        {facts.map(([label, value]) => (
          <MDBox key={label} sx={{ minWidth: 0 }}>
            <MDBox component="dt" sx={{ fontSize: 12, color: t.text2 }}>{label}</MDBox>
            <MDBox component="dd" sx={{ m: 0, fontSize: 14, fontWeight: 600, color: t.text, fontVariantNumeric: "tabular-nums" }}>{value}</MDBox>
          </MDBox>
        ))}
      </MDBox>
      <MDButton variant="gradient" color="info" size="small" onClick={onUpgrade}>
        Upgrade licence
      </MDButton>
    </MDBox>
  );
}

LicenceSummaryV2.propTypes = {
  licenseInfo: PropTypes.object.isRequired,
  onUpgrade: PropTypes.func.isRequired,
};

function UserSettings() {
  const isV2 = useTheme()?.pearlUi === 'v2';
  const { axiosInstance, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);

  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [newUser, setNewUser] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'STANDARD_USER',
    base_role_id: null,
    addon_role_ids: [],
    manager_id: null,
    department: '',
    designation: '',
  });

  const [licenseInfo, setLicenseInfo] = useState({
    license_type: '',
    expiry_date: '',
    total_seats: 0,
    used_seats: 0,
    seats_remaining: 0,
    is_trial: false,
    remaining_days: 0,
    billing_cycle: '',
    status: '',
    usage_percentage: 0,
    is_expiring_soon: false,
    is_expired: false
  });

  // Log download dialog state
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [logUser, setLogUser] = useState(null);
  const [logDateRange, setLogDateRange] = useState([moment().subtract(7, 'days'), moment()]);
  const [logDownloading, setLogDownloading] = useState(false);
  const [isAllLogs, setIsAllLogs] = useState(false);

  // The tenant's real roles. Empty on a tenant that has not run migration 030,
  // which is what legacyRoles below is for.
  const [roles, setRoles] = useState([]);
  const legacyRoles = ["SYSTEM_ADMIN", "MANAGER", "STANDARD_USER"];
  const hasRealRoles = roles.length > 0;

  useEffect(() => {
    fetchUsers();
    fetchLicenseInfo();
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get('/users/assignable-roles');
      setRoles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Not fatal: the modal falls back to the legacy three rather than
      // refusing to open. A user admin who cannot list roles can still create
      // users, which is the behaviour that existed before roles did.
      console.error('Error fetching assignable roles:', error);
      setRoles([]);
    }
  };

  const fetchLicenseInfo = async () => {
    try {
      const response = await axiosInstance.get('/license/info');
      setLicenseInfo(response.data);
    } catch (error) {
      console.error('Error fetching license info:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch license information',
        severity: 'error',
      });
    }
  };


  const handleUserUpdate = (updatedUser) => {
    // Refresh the entire users list
    fetchUsers(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const fetchUsers = async (shouldShowSnackbar = true) => {
    try {
      const response = await axiosInstance.get('/users/');
      setUsers(response.data);

      if (shouldShowSnackbar) {
        // setSnackbar({
        //   open: true,
        //   message: 'Users list updated',
        //   severity: 'success',
        // });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch users',
        severity: 'error',
      });
    }
  };

  const handleAddUser = async () => {
    if (isCreatingUser) return;

    setLastNameError('');
    setEmailError('');

    // Validation
    if (!newUser.last_name) {
      setLastNameError('Last Name is required');
      setSnackbar({
        open: true,
        message: 'Last Name is required',
        severity: 'error',
      });
      return;
    }

    if (!newUser.email) {
      setEmailError('Email is required');
      setSnackbar({
        open: true,
        message: 'Email is required',
        severity: 'error',
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      setEmailError('Invalid email format');
      setSnackbar({
        open: true,
        message: 'Invalid email format',
        severity: 'error',
      });
      return;
    }

    // A user with no base role resolves to an empty grant set and sees nothing
    // at all once the tenant enforces, so refuse here rather than create one.
    if (hasRealRoles && !newUser.base_role_id) {
      setSnackbar({
        open: true,
        message: 'Choose a role for this person',
        severity: 'error',
      });
      return;
    }

    setIsCreatingUser(true);

    try {
      const userToAdd = {
        email: newUser.email,
        // base_role_id is the real grant; the backend derives users.role from
        // it. The legacy string only goes up on tenants with no roles table.
        ...(hasRealRoles
          ? {
            base_role_id: newUser.base_role_id,
            addon_role_ids: newUser.addon_role_ids,
          }
          : { role: newUser.role.toLowerCase() }),
        is_active: true,
        department: newUser.department || null,
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        designation: newUser.designation || null,
        reset_link: `${window.location.origin}/create-password`,
        manager_id: newUser.manager_id
      };

      const response = await axiosInstance.post('/users/', userToAdd);

      // Handle successful response
      if (response.data) {
        const createdUser = response.data;

        // Log user creation
        await frontendLogger.logCreate('User', {
          id: createdUser.id,
          username: createdUser.username,
          email: createdUser.email,
          created_by: user.username,
          timestamp: new Date().toISOString(),
        });

        // Update users list with new user
        setUsers(prevUsers => [...prevUsers, createdUser]);

        // Close dialog
        setOpenAddUserDialog(false);

        // Show success message
        setSnackbar({
          open: true,
          message: `User ${createdUser.first_name} ${createdUser.last_name} created successfully with username ${createdUser.username}!`,
          severity: 'success',
        });

        // Reset form
        setNewUser({
          email: '',
          first_name: '',
          last_name: '',
          role: "STANDARD_USER",
          base_role_id: null,
          addon_role_ids: [],
          manager_id: null,
          department: '',
          designation: '',
        });
      }
    } catch (error) {
      console.error('Error adding user:', error);

      // Check for license-related errors first
      const errorDetail = error.response?.data?.detail;
      if (errorDetail?.error_code) {
        switch (errorDetail.error_code) {
          case 'LICENSE_LIMIT_REACHED':
            setSnackbar({
              open: true,
              message: errorDetail.message,
              severity: 'warning',
              action: (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    // You can replace this URL with your actual sales contact page
                    window.open('https://your-domain.com/contact-sales', '_blank');
                  }}
                >
                  Contact Sales
                </Button>
              ),
              autoHideDuration: 10000, // Give more time to see the message
            });
            break;

          case 'LICENSE_EXPIRED':
            setSnackbar({
              open: true,
              message: `Your license expired on ${new Date(errorDetail.details.expiry_date).toLocaleDateString()}. Please renew to continue.`,
              severity: 'error',
              action: (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    window.open('https://your-domain.com/renew-license', '_blank');
                  }}
                >
                  Renew License
                </Button>
              ),
              autoHideDuration: 10000,
            });
            break;

          case 'NO_ACTIVE_LICENSE':
            setSnackbar({
              open: true,
              message: errorDetail.message,
              severity: 'error',
              action: (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    window.open('https://your-domain.com/activate-license', '_blank');
                  }}
                >
                  Activate License
                </Button>
              ),
              autoHideDuration: 10000,
            });
            break;

          default:
            // Handle other license-related errors
            setSnackbar({
              open: true,
              message: errorDetail.message,
              severity: 'error',
            });
        }
      } else {
        // Handle non-license errors (existing error handling)
        let errorMessage = 'Failed to create user';

        if (error.response) {
          switch (error.response.status) {
            case 400:
              errorMessage = error.response.data.detail || 'Invalid input data';
              break;
            case 401:
              errorMessage = 'Unauthorized access';
              break;
            case 403:
              errorMessage = 'You do not have permission to create users';
              break;
            case 409:
              errorMessage = 'Email already exists';
              break;
            case 422:
              errorMessage = 'Invalid data format';
              break;
            case 500:
              errorMessage = 'Server error occurred';
              break;
            default:
              errorMessage = error.response.data.detail || 'An error occurred while creating the user';
          }
        }

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });

        // If it's a validation error, set specific field errors
        if (error.response?.status === 422) {
          const validationErrors = error.response.data.detail;
          if (validationErrors.email) {
            setEmailError(validationErrors.email);
          }
          if (validationErrors.last_name) {
            setLastNameError(validationErrors.last_name);
          }
        }
      }
    } finally {
      setIsCreatingUser(false);
      fetchUsers();
    }
  };

  const handleCancelAddUser = () => {
    setOpenAddUserDialog(false);
    setNewUser({
      email: '',
      first_name: '',
      last_name: '',
      role: "STANDARD_USER",
      base_role_id: null,
      addon_role_ids: [],
      manager_id: null,
      department: '',
      designation: '',
    });
    setLastNameError('');
    setEmailError('');
  };

  const handleToggleUserStatus = async (userId, isActive) => {
    try {
      const newActiveStatus = !isActive;
      await axiosInstance.patch(`/users/${userId}/status`, { is_active: newActiveStatus });
      await fetchUsers();
      setSnackbar({
        open: true,
        message: `User status ${newActiveStatus ? 'activated' : 'deactivated'} successfully`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating user status:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to update user status',
        severity: 'error',
      });
    }
  };

  const getFullName = (user) => {
    const nameParts = [
      user.first_name || '',
      user.last_name || ''
    ].filter(part => part.trim() !== '');
    return nameParts.join(' ').trim() || user.username;
  };

  const findManagerById = (managerId) => {
    return users.find(u => u.id === managerId) || null;
  };


  const handleUpgradeLicense = () => {
    if (!user) {
      return;
    }

    const emailSubject = `License Upgrade Request - ${licenseInfo.license_type} License`;
    
    const emailBody = `
  Hello Support Team,
  
  I would like to request a license upgrade for our organization.
  
  Current License Details:
  - License Type: ${licenseInfo.license_type}
  - Expiry Date: ${new Date(licenseInfo.expiry_date).toLocaleDateString()}
  - Remaining Days: ${licenseInfo.remaining_days}
  
  Current Usage:
  - Seats Used: ${licenseInfo.used_seats}/${licenseInfo.total_seats}
  - Seats Available: ${licenseInfo.seats_remaining}
  
  Request initiated by:
  - Username: ${user.username}
  - Role: ${user.role}
  
  Please provide information about available upgrade options and pricing.
  
  Thank you,
  ${user.username}
  `;
  
    const mailtoLink = `mailto:support@pearlcrm.com?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    window.location.href = mailtoLink;
  };

  // Handler for Download Logs button
  const handleDownloadLogs = (user) => {
    setLogUser(user);
    setIsAllLogs(false);
    setLogDialogOpen(true);
    setLogDateRange([moment().subtract(7, 'days'), moment()]);
  };

  // Handler for Download All Logs button
  const handleDownloadAllLogs = () => {
    setLogUser(null);
    setIsAllLogs(true);
    setLogDialogOpen(true);
    setLogDateRange([moment().subtract(7, 'days'), moment()]);
  };

  // Download logs API call
  const handleLogDownloadConfirm = async () => {
    setLogDownloading(true);
    try {
      const [startDate, endDate] = logDateRange;
      const response = await axiosInstance.get('/log/download', {
        params: {
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD'),
          username: isAllLogs ? undefined : logUser.username
        },
        responseType: 'blob'
      });
      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = isAllLogs 
        ? `all_logs_${startDate.format('YYYY-MM-DD')}_to_${endDate.format('YYYY-MM-DD')}.txt`
        : `logs_${logUser.username}_${startDate.format('YYYY-MM-DD')}_to_${endDate.format('YYYY-MM-DD')}.txt`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSnackbar({ open: true, message: 'Logs downloaded successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'You cannot download logs for more than 30 days. Please select a shorter date range.', severity: 'error' });
    } finally {
      setLogDownloading(false);
      setLogDialogOpen(false);
    }
  };

  return (
    <MDBox>

      {isV2 ? (
        <LicenceSummaryV2 licenseInfo={licenseInfo} onUpgrade={handleUpgradeLicense} />
      ) : (
      <Grid container spacing={2} mb={-2}>
        <Grid item xs={12}>
          <MDBox
            bgcolor="white"
            borderRadius="lg"
            p={2}
            boxShadow={2}
          >
            <Grid container spacing={2}>
              {/* License Information */}
              <Grid item xs={12} md={4}>
                <MDBox
                  display="flex"
                  flexDirection="column"
                  p={2}
                  border="1px solid #e0e0e0"
                  borderRadius="lg"
                  height="100%"
                >
                  <MDBox color="text" fontWeight="bold" fontSize="0.875rem" mb={1}>
                    License Details
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between" mb={1}>
                    <MDBox>Type:</MDBox>
                    <MDBox color="info" fontWeight="bold">
                      {licenseInfo.license_type}
                      {licenseInfo.is_trial && " (Trial)"}
                    </MDBox>
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between" mb={1}>
                    <MDBox>Status:</MDBox>
                    <MDBox 
                      color={
                        licenseInfo.status === "Active" ? "success" :
                        licenseInfo.status === "Expiring Soon" ? "warning" : "error"
                      }
                      fontWeight="bold"
                    >
                      {licenseInfo.status}
                    </MDBox>
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between">
                    <MDBox>Billing Cycle:</MDBox>
                    <MDBox fontWeight="bold">{licenseInfo.billing_cycle}</MDBox>
                  </MDBox>
                </MDBox>
              </Grid>

              {/* Usage Statistics */}
              <Grid item xs={12} md={4}>
                <MDBox
                  display="flex"
                  flexDirection="column"
                  p={2}
                  border="1px solid #e0e0e0"
                  borderRadius="lg"
                  height="100%"
                >
                  <MDBox color="text" fontWeight="bold" fontSize="0.875rem" mb={1}>
                    Usage Statistics
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between" mb={1}>
                    <MDBox>Seats Used:</MDBox>
                    <MDBox fontWeight="bold">
                      {licenseInfo.used_seats}/{licenseInfo.total_seats} ({Number(licenseInfo.usage_percentage || 0).toFixed(1)}%)
                    </MDBox>
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between">
                    <MDBox>Available Licenses:</MDBox>
                    <MDBox fontWeight="bold" color="success">
                      {licenseInfo.seats_remaining}
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Grid>

              {/* Expiration Info */}
              <Grid item xs={12} md={4}>
                <MDBox
                  display="flex"
                  flexDirection="column"
                  p={2}
                  border="1px solid #e0e0e0"
                  borderRadius="lg"
                  height="100%"
                >
                  <MDBox color="text" fontWeight="bold" fontSize="0.875rem" mb={1}>
                    Expiration Details
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between" mb={1}>
                    <MDBox>Expiry Date:</MDBox>
                    <MDBox fontWeight="bold">
                      {new Date(licenseInfo.expiry_date).toLocaleDateString()}
                    </MDBox>
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between" mb={2}>
                    <MDBox>Days Remaining:</MDBox>
                    <MDBox 
                      fontWeight="bold"
                      color={licenseInfo.is_expiring_soon ? "warning" : "success"}
                    >
                      {licenseInfo.remaining_days}
                    </MDBox>
                  </MDBox>
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    onClick={handleUpgradeLicense}
                    startIcon={<i className="fas fa-arrow-up" />}
                    fullWidth
                  >
                    Upgrade License
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </Grid>
      </Grid>
      )}

      {isV2 ? (
        <UsersAndSeatsV2
          reloadToken={users.length}
          onInvite={() => setOpenAddUserDialog(true)}
          onDownloadAllLogs={handleDownloadAllLogs}
        />
      ) : (
      <>
      <Grid container>
        <Grid item xs={12}>
          {/* Renders nothing unless the tenant bought the Call Center add-on. */}
          <CallCenterSeats users={users} />
          {/* Renders nothing unless the tenant bought the WhatsApp add-on. */}
          <WhatsAppSeats users={users} />
          {/* Renders nothing unless the tenant bought the AI Assistant add-on. */}
          <AiAssistantSeats users={users} embedded />
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={12}>
          <UserTable
            users={users}
            handleToggleUserStatus={handleToggleUserStatus}
            onAddUser={() => setOpenAddUserDialog(true)}
            onUserUpdate={handleUserUpdate}
            onDownloadLogs={handleDownloadLogs}
            onDownloadAllLogs={handleDownloadAllLogs}
          />
        </Grid>
      </Grid>
      </>
      )}

      <Dialog
        open={openAddUserDialog}
        onClose={() => setOpenAddUserDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={newUser.first_name}
                onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                margin="normal"
                disabled={isCreatingUser}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={newUser.last_name}
                onChange={(e) => {
                  setNewUser({ ...newUser, last_name: e.target.value });
                  setLastNameError('');
                }}
                margin="normal"
                required
                error={!!lastNameError}
                helperText={lastNameError}
                disabled={isCreatingUser}
              />
              <TextField
                fullWidth
                label="Designation"
                value={newUser.designation}
                onChange={(e) => setNewUser({ ...newUser, designation: e.target.value })}
                margin="normal"
                disabled={isCreatingUser}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newUser.email}
                onChange={(e) => {
                  setNewUser({ ...newUser, email: e.target.value });
                  setEmailError('');
                }}
                margin="normal"
                required
                error={!!emailError}
                helperText={emailError}
                disabled={isCreatingUser}
              />
              {hasRealRoles ? (
                <RolePicker
                  roles={roles}
                  baseRoleId={newUser.base_role_id}
                  addonRoleIds={newUser.addon_role_ids}
                  onBaseRoleChange={(id) => setNewUser({
                    ...newUser,
                    base_role_id: id,
                    // Add-ons are orthogonal to the base role, so changing the
                    // base keeps them -- but clearing it leaves nothing for
                    // them to widen.
                    addon_role_ids: id ? newUser.addon_role_ids : [],
                  })}
                  onAddonRolesChange={(ids) => setNewUser({ ...newUser, addon_role_ids: ids })}
                  disabled={isCreatingUser}
                  required
                />
              ) : (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={newUser.role}
                    label="Role"
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    disabled={isCreatingUser}
                  >
                    {legacyRoles.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <TextField
                fullWidth
                label="Department"
                value={newUser.department}
                onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                margin="normal"
                disabled={isCreatingUser}
              />
              <Autocomplete
                fullWidth
                options={users}
                getOptionLabel={(option) => getFullName(option)}
                value={findManagerById(newUser.manager_id)}
                onChange={(_, newValue) => {
                  setNewUser({ ...newUser, manager_id: newValue ? newValue.id : null });
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    height: '45px',
                  },
                  '& .MuiInputBase-input': {
                    height: '25px',
                    padding: '0 15px !important',
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Manager"
                    margin="normal"
                    InputProps={{
                      ...params.InputProps,
                      style: { height: '45px' }
                    }}
                    disabled={isCreatingUser}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => handleCancelAddUser()}
            color="secondary"
            disabled={isCreatingUser}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleAddUser}
            color="info"
            variant="gradient"
            disabled={isCreatingUser}
          >
            {isCreatingUser ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Creating...
              </>
            ) : (
              'Add User'
            )}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Log Download Dialog */}
      <Dialog open={logDialogOpen} onClose={() => setLogDialogOpen(false)}>
        <DialogTitle>
          {isAllLogs ? 'Download All User Logs' : 'Download User Logs'}
        </DialogTitle>
        <DialogContent>
          {!isAllLogs && (
            <div style={{ marginBottom: 16 }}>
              <b>User:</b> {logUser?.username}
            </div>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Start Date"
                type="date"
                value={logDateRange[0] ? logDateRange[0].format('YYYY-MM-DD') : ''}
                onChange={e => setLogDateRange([moment(e.target.value), logDateRange[1]])}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: moment().format('YYYY-MM-DD') }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End Date"
                type="date"
                value={logDateRange[1] ? logDateRange[1].format('YYYY-MM-DD') : ''}
                onChange={e => setLogDateRange([logDateRange[0], moment(e.target.value)])}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: moment().format('YYYY-MM-DD') }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogDialogOpen(false)} disabled={logDownloading}>Cancel</Button>
          <Button onClick={handleLogDownloadConfirm} color="primary" disabled={logDownloading}>
            {logDownloading ? 'Downloading...' : 'Download'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MDBox>
  );
}

export default UserSettings;